import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getProducts } from '../store/api/products';
import Loader from '../components/common/loader';
import AddToCartButton from '../components/cart/addToCartButton';
import WishlistButton from '../components/wishlist/wishlistButton';

const ProductListing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 12
  });
  
  // Quantity controls for each product
  const [quantities, setQuantities] = useState({});
  
  // Filters
  const [filters, setFilters] = useState({
    category: '',
    subCategory: '',
    minPrice: '',
    maxPrice: '',
    color: '',
    material: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Parse query params from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page') || 1;
    const category = searchParams.get('category') || '';
    const subCategory = searchParams.get('subCategory') || '';
    const search = searchParams.get('search') || '';
    const color = searchParams.get('color') || '';
    const material = searchParams.get('material') || '';
    const minPrice = searchParams.get('minPrice') || '';
    const maxPrice = searchParams.get('maxPrice') || '';
    const sortBy = searchParams.get('sortBy') || 'createdAt';
    const sortOrder = searchParams.get('sortOrder') || 'desc';
    
    setFilters({
      ...filters,
      category,
      subCategory,
      search,
      color,
      material,
      minPrice,
      maxPrice,
      sortBy,
      sortOrder
    });
    
    fetchProducts(page, {
      category,
      subCategory,
      search,
      color,
      material,
      minPrice,
      maxPrice,
      sortBy,
      sortOrder
    });
  }, [location.search]);

  // Update quantities when products change
  useEffect(() => {
    const newQuantities = {};
    products.forEach(product => {
      if (!quantities[product.productId]) {
        newQuantities[product.productId] = product.minOrderQuantity || 1;
      }
    });
    if (Object.keys(newQuantities).length > 0) {
      setQuantities(prev => ({ ...prev, ...newQuantities }));
    }
  }, [products]);
  
  // Fetch products with current filters
  const fetchProducts = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      
      const queryParams = {
        page,
        limit: 12,
        ...currentFilters
      };
      
      // Remove empty filter values
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key]) delete queryParams[key];
      });
      
      const response = await getProducts(queryParams);
      
      setProducts(response.data.products);
      setPagination(response.data.pagination);
      
      // Initialize quantities for each product
      const initialQuantities = {};
      response.data.products.forEach(product => {
        initialQuantities[product.productId] = product.minOrderQuantity || 1;
      });
      setQuantities(initialQuantities);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load products. Please try again.');
      setLoading(false);
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  // Apply filters
  const applyFilters = () => {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.set(key, value);
    });
    
    navigate(`/products?${queryParams.toString()}`);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: '',
      subCategory: '',
      minPrice: '',
      maxPrice: '',
      color: '',
      material: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    
    navigate('/products');
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('page', newPage);
    navigate(`/products?${queryParams.toString()}`);
  };
  
  // Calculate discount percentage
  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // Format category/subcategory name
  const formatName = (name) => {
    return name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  // Quantity handlers
  const handleQuantityChange = (productId, e) => {
    const value = parseInt(e.target.value);
    if (value > 0) {
      setQuantities(prev => ({
        ...prev,
        [productId]: value
      }));
    }
  };

  const incrementQuantity = (productId) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: prev[productId] + 1
    }));
  };

  const decrementQuantity = (productId, minQty) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(minQty, prev[productId] - 1)
    }));
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters sidebar */}
        <div className="w-full md:w-1/4 bg-white rounded-lg shadow p-4">
          <h2 className="text-xl font-semibold mb-4">Filters</h2>
          
          <div className="space-y-4">
            {/* Category filter - UPDATED */}
            <div>
              <label className="block text-gray-700 mb-2">Category</label>
              <select 
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              >
                <option value="">All Categories</option>
                <option value="AGRICULTURE_PRODUCTS">Agriculture Products</option>
                <option value="PACKAGING_PRODUCTS">Packaging Products</option>
              </select>
            </div>

            {/* Sub-Category filter - NEW */}
            {filters.category && (
              <div>
                <label className="block text-gray-700 mb-2">Sub-Category</label>
                <select 
                  name="subCategory"
                  value={filters.subCategory}
                  onChange={handleFilterChange}
                  className="w-full border rounded p-2"
                >
                  <option value="">All Sub-Categories</option>
                  {filters.category === 'AGRICULTURE_PRODUCTS' && (
                    <>
                      <option value="MULCHING_FILM">Mulching Film</option>
                      <option value="NURSERY_SHEETS">Nursery Sheets</option>
                      
                    </>
                  )}
                  {filters.category === 'PACKAGING_PRODUCTS' && (
                    <>
                      <option value="COURIER_BAGS">Courier Bags</option>
                      <option value="PAPER_BAGS">Paper Bags</option>
                      
                    </>
                  )}
                </select>
              </div>
            )}

            {/* Material filter - NEW */}
            {/* <div>
              <label className="block text-gray-700 mb-2">Material</label>
              <select 
                name="material"
                value={filters.material}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              >
               <option value="">All Materials</option>
                <option value="LLDPE">LLDPE</option>
                <option value="LDPE">LDPE</option>
                <option value="KRAFT_PAPER">Kraft Paper</option>
                <option value="BLEACHED_PAPER">Bleached Paper</option>
              </select>
            </div> */}

            {/* Color filter - NEW */}
            <div>
              <label className="block text-gray-700 mb-2">Color</label>
              <select 
                name="color"
                value={filters.color}
                onChange={handleFilterChange} 
                className="w-full border rounded p-2"
              >
                  <option value="">All Colors</option>
                <option value="WHITE_AND_BLACK">White and Black</option>
                <option value="SILVER_AND_BLACK">Silver and Black</option>
                <option value="TRANSPARENT">Transparent</option>
                <option value="BROWN">Brown</option>
                <option value="WHITE">White</option>
              </select>
            </div>
            
            {/* Price range */}
            <div>
              <label className="block text-gray-700 mb-2">Price Range</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  name="minPrice"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  className="w-1/2 border rounded p-2"
                />
                <span>-</span>
                <input
                  type="number"
                  name="maxPrice"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  className="w-1/2 border rounded p-2"
                />
              </div>
            </div>
            
            {/* Brand filter */}
            {/* <div>
              <label className="block text-gray-700 mb-2">Brand</label>
              <input
                type="text"
                name="brand"
                placeholder="Enter brand name"
                value={filters.brand}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              />
            </div>
             */}
            {/* Sort options */}
            <div>
              <label className="block text-gray-700 mb-2">Sort By</label>
              <select
                name="sortBy"
                value={filters.sortBy}
                onChange={handleFilterChange}
                className="w-full border rounded p-2"
              >
                <option value="createdAt">Latest</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="name">Name A-Z</option>
                <option value="popular">Popular</option>
              </select>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={applyFilters}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition w-full"
              >
                Apply Filters
              </button>
              <button
                onClick={clearFilters}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        
        {/* Product grid */}
        <div className="w-full md:w-3/4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader size="lg" />
            </div>
          ) : error ? (
            <div className="bg-red-100 text-red-700 p-4 rounded">
              {error}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-yellow-100 text-yellow-800 p-4 rounded">
              No products found matching your criteria.
            </div>
          ) : (
            <>
              {/* Results count */}
              <div className="mb-4 text-gray-600">
                Showing {products.length} of {pagination.total} products
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {products.map(product => (
                  <div key={product.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition">
                    <div className="relative">
                      <Link to={`/product/${product.productId}`}>
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={product.imageUrl || '/images/placeholder.png'} 
                            alt={product.name} 
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </Link>
                      
                      {/* Stock Badge - NEW */}
                      {product.stockStatus === 'LOW_STOCK' && (
                        <span className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded font-medium">
                          Low Stock
                        </span>
                      )}
                      {product.stockStatus === 'OUT_OF_STOCK' && (
                        <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                          Out of Stock
                        </span>
                      )}

                      {/* Eco-Friendly Badge - NEW */}
                      {product.isEcoFriendly && (
                        <span className="absolute top-2 right-10 bg-green-500 text-white text-xs px-2 py-1 rounded font-medium">
                          🌱 Eco
                        </span>
                      )}
                      
                      <div className="absolute top-2 right-2">
                        <WishlistButton productId={product.productId} />
                      </div>
                    </div>
                    
                    <Link to={`/product/${product.productId}`}>
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 mb-1 truncate">
                          {product.name}
                        </h3>
                        
                       
                         
                          {product.manufacturer && (
                            <p>By {product.manufacturer}</p>
                          )}
                        

                        {/* Material & Color - NEW */}
                        <div className="flex gap-2 mb-2 text-xs">
                          {product.material && (
                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {formatName(product.material)}
                            </span>
                          )}
                          {product.color && (
                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              {formatName(product.color)}
                            </span>
                          )}
                        </div>
                        
                                 <div className="flex items-end mb-2 flex-wrap gap-1">
                          {product.hasSizeVariants ? (
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">
                                {product.startingSize} · {product.startingPack} pcs/pack
                              </p>
                              <span className="text-sm text-gray-500">From </span>
                              <span className="text-lg font-bold text-gray-900">
                                ₹{product.price?.toLocaleString('en-IN')}
                              </span>
                              {product.mrp > product.price && (
                                <span className="text-sm text-gray-500 line-through ml-2">
                                  ₹{product.mrp?.toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                          ) : (
                            <>
                              <span className="text-lg font-bold text-gray-900">
                                ₹{product.price?.toLocaleString('en-IN')}
                              </span>
                              {product.mrp > product.price && (
                                <>
                                  <span className="text-sm text-gray-500 line-through ml-2">
                                    ₹{product.mrp?.toLocaleString('en-IN')}
                                  </span>
                                  <span className="text-sm text-green-600 ml-2">
                                    {calculateDiscount(product.mrp, product.price)}% off
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </div>

                        {/* Bulk Pricing Indicator - NEW */}
                        {product.hasBulkPricing && (
                          <p className="text-xs text-blue-600 mb-2">
                            💰 Bulk pricing from {product.bulkStartsFrom}+ units
                          </p>
                        )}

                        {/* Min Order Quantity - NEW */}
                        {product.minOrderQuantity > 1 && (
                          <p className="text-xs text-gray-500">
                            Min Order: {product.minOrderQuantity} units
                          </p>
                        )}
                      </div>
                    </Link>
                    
                   <div className="px-4 pb-4">
                      {product.hasSizeVariants ? (
                        <Link
                          to={`/product/${product.productId}`}
                          className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg transition"
                        >
                          Choose Size & Pack
                        </Link>
                      ) : (
                        <>
                          <div className="mb-3">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center border rounded-md">
                                <button
                                  onClick={() => decrementQuantity(product.productId, product.minOrderQuantity || 1)}
                                  disabled={quantities[product.productId] <= (product.minOrderQuantity || 1)}
                                  className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                                >−</button>
                                <input
                                  type="number"
                                  value={quantities[product.productId] || 1}
                                  onChange={(e) => handleQuantityChange(product.productId, e)}
                                  min={product.minOrderQuantity || 1}
                                  className="w-12 text-center border-x py-1 focus:outline-none text-sm"
                                />
                                <button
                                  onClick={() => incrementQuantity(product.productId)}
                                  className="px-2 py-1 text-gray-600 hover:bg-gray-100 transition font-medium"
                                >+</button>
                              </div>
                              {(product.minOrderQuantity || 1) > 1 && quantities[product.productId] < (product.minOrderQuantity || 1) && (
                                <span className="text-xs text-red-500">Min: {product.minOrderQuantity}</span>
                              )}
                              {(product.minOrderQuantity || 1) > 1 && quantities[product.productId] >= (product.minOrderQuantity || 1) && (
                                <span className="text-xs text-green-600">✓ Ready</span>
                              )}
                            </div>
                          </div>
                          <AddToCartButton
                            product={product}
                            quantity={quantities[product.productId] || 1}
                            fullWidth={true}
                            showQuantity={false}
                            disabled={product.stockStatus === 'OUT_OF_STOCK'}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center mt-8">
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className={`px-3 py-1 rounded ${
                        pagination.page === 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`px-3 py-1 rounded ${
                          pagination.page === i + 1
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className={`px-3 py-1 rounded ${
                        pagination.page === pagination.pages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListing;